"use client"

import { useState } from "react"
import {
  Box,
  Text,
  Group,
  Badge,
  Button,
  Card,
  NumberInput,
  Divider,
  Stack,
} from "@forestsync/ui"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { BuyCreditsModal } from "@/components/browse/BuyCreditsModal"
import type { MarketSummary, SinkDetail } from "@/lib/queries"
import type { Sink } from "@/components/shared/ProjectCard"

interface MarketBuyBoxProps {
  market: MarketSummary
  sink: SinkDetail | null
  isAuthenticated: boolean
  vintages?: MarketSummary[]
}

export function MarketBuyBox({
  market,
  sink,
  isAuthenticated,
  vintages = [],
}: MarketBuyBoxProps) {
  const t = useTranslations("trading")
  const [buyModalOpen, setBuyModalOpen] = useState(false)
  const [tonsToOffset, setTonsToOffset] = useState<number | string>(1)

  const pricePerTon = market.bestAsk ?? market.lastPrice ?? 0
  const validTons =
    typeof tonsToOffset === "number" ? Math.max(1, tonsToOffset) : 1
  const subtotal = validTons * pricePerTon

  return (
    <Box pos="sticky" style={{ top: 100 }}>
      <Card withBorder shadow="md" radius="md" padding="xl">
        {vintages.length > 1 && (
          <>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
              {t("storefront.vintage")}
            </Text>
            <Group gap="xs" mb="lg" wrap="wrap">
              {[...vintages]
                .sort((a, b) => b.vintage - a.vintage)
                .map((v) => (
                  <Button
                    key={v.vintage}
                    component={Link}
                    href={`/market/${v.projectRefId}/${v.vintage}`}
                    variant={v.vintage === market.vintage ? "filled" : "outline"}
                    color="green"
                    autoContrast
                    size="xs"
                    px="sm"
                  >
                    {v.vintage}
                    {v.bestAsk ? ` · From €${v.bestAsk.toFixed(2)}` : ""}
                    {v.askDepth > 0
                      ? ` · ${Math.round(v.askDepth).toLocaleString()}t`
                      : ""}
                  </Button>
                ))}
            </Group>
          </>
        )}

        <Text
          size="xl"
          fw={700}
          mb={market.bestAsk && pricePerTon > 0 ? 2 : "xs"}
        >
          {pricePerTon > 0
            ? `${market.bestAsk ? "From " : ""}€${pricePerTon.toFixed(2)}`
            : "N/A"}{" "}
          <Text component="span" size="sm" c="dimmed" fw={400}>
            / {t("storefront.perTon")}
          </Text>
        </Text>
        {market.bestAsk && pricePerTon > 0 && (
          <Text size="xs" c="dimmed" mb="xs">
            {t("storefront.priceVariesNote")}
          </Text>
        )}

        {isAuthenticated ? (
          <>
            <NumberInput
              label={t("storefront.tonsToOffset")}
              value={tonsToOffset}
              onChange={setTonsToOffset}
              min={1}
              step={1}
              decimalScale={0}
              size="md"
              mt="md"
              mb="xl"
            />
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  €{pricePerTon.toFixed(2)} x {validTons}{" "}
                  {t("storefront.perTon")}s
                </Text>
                <Text size="sm">€{subtotal.toFixed(2)}</Text>
              </Group>
              <Divider my="sm" />
              <Group justify="space-between">
                <Text fw={700}>{t("totalDue")}</Text>
                <Text fw={700}>€{subtotal.toFixed(2)}</Text>
              </Group>
            </Stack>
            <Box mt="xl">
              <Button
                fullWidth
                size="lg"
                color="green"
                autoContrast
                onClick={() => setBuyModalOpen(true)}
                disabled={!validTons || pricePerTon === 0}
              >
                {t("storefront.buyNow")}
              </Button>
            </Box>
          </>
        ) : (
          <Box mt="xl">
            <Button
              component={Link}
              href={`/login?next=/market/${market.projectRefId}/${market.vintage}`}
              fullWidth
              size="lg"
              color="green"
              autoContrast
            >
              {t("storefront.signInToBuy")}
            </Button>
          </Box>
        )}

        {pricePerTon === 0 && (
          <Text size="xs" c="dimmed" ta="center" mt="sm">
            {t("noSellers")}
          </Text>
        )}
        <Text size="xs" c="dimmed" ta="center" mt="sm">
          {t("storefront.wontBeCharged")}
        </Text>
      </Card>

      {isAuthenticated && (
        <BuyCreditsModal
          sink={buyModalOpen && sink ? (sink as unknown as Sink) : null}
          vintage={market.vintage}
          creditType={market.creditType ?? ""}
          onClose={() => setBuyModalOpen(false)}
          initialVolume={validTons}
          skipEntry={true}
        />
      )}
    </Box>
  )
}
